import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./AddVehicleModal.css";
import { firebaseUploadVehicleImage } from "../../services/firebaseUpload";
import { serviceRegisterVehicle } from "../../api/serviceFleetMaster";

const initialVehicleState = {
  vehicleCode: "",
  trackerCode: "",

  steerMaxWeight: "",
  driveMaxWeight: "",
  trailerAMaxWeight: "",
  trailerBMaxWeight: "",
  weightUnit: "kg",

  voltage: "",
  voltageUnit: "volt",

  fuel: "",
  fuelUnit: "liters",
  mileage: "",

  servicedOn: "",
  nextServiceOn: "",
};

const getLocalDateInputValue = () => {
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset() * 60_000;

  return new Date(currentDate.getTime() - timezoneOffset)
    .toISOString()
    .split("T")[0];
};

const AddVehicleModal = ({ onClose, onVehicleAdded }) => {
  const fileInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [updating, setUpdating] = useState(false);
  const [newVehicle, setNewVehicle] = useState(initialVehicleState);
  const [regoUrgent, setRegoUrgent] = useState(false);

  const currentDate = getLocalDateInputValue();

  /*
   * Create a temporary browser URL for the selected image and
   * revoke it whenever the image changes or the modal unmounts.
   */
  useEffect(() => {
    if (!image) {
      setImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(image);

    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [image]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setNewVehicle((currentVehicle) => ({
      ...currentVehicle,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      event.target.value = "";
      return;
    }

    setImage(selectedFile);
  };

  const openFilePicker = () => {
    if (updating) {
      return;
    }

    fileInputRef.current?.click();
  };

  const validateVehicle = () => {
    if (!newVehicle.vehicleCode.trim()) {
      setRegoUrgent(true);
      throw new Error("Registration (Rego) is required.");
    }

    if (
      !newVehicle.weightUnit ||
      !newVehicle.fuelUnit ||
      !newVehicle.voltageUnit
    ) {
      throw new Error("Missing measurement units.");
    }

    if (
      newVehicle.servicedOn &&
      newVehicle.nextServiceOn &&
      newVehicle.nextServiceOn < newVehicle.servicedOn
    ) {
      throw new Error(
        "Next service date cannot be before the last service date.",
      );
    }
  };

  const serverAddVehicle = async () => {
    try {
      validateVehicle();
      setUpdating(true);

      const vehicleCode = newVehicle.vehicleCode.trim();

      /*
       * Upload only when the user has selected an image.
       */
      const imageUrl = null;
      // const imageUrl = image
      //   ? await firebaseUploadVehicleImage({
      //       organizationId,
      //       vehicleCode,
      //       file: image,
      //     })
      //   : null;

      const payload = {
        vehicleCode,

        trackerCode: newVehicle.trackerCode.trim() || null,

        steerMaxWeight: newVehicle.steerMaxWeight || null,

        driveMaxWeight: newVehicle.driveMaxWeight || null,

        trailerAMaxWeight: newVehicle.trailerAMaxWeight || null,

        trailerBMaxWeight: newVehicle.trailerBMaxWeight || null,

        weightUnit: newVehicle.weightUnit,

        voltage: newVehicle.voltage || null,
        voltageUnit: newVehicle.voltageUnit,

        fuel: newVehicle.fuel || null,
        fuelUnit: newVehicle.fuelUnit,
        mileage: newVehicle.mileage || null,

        servicedOn: newVehicle.servicedOn || null,
        nextServiceOn: newVehicle.nextServiceOn || null,

        imageUrl: imageUrl || null,
      };

      if (import.meta.env.DEV) {
        console.log("Add vehicle payload:", payload);
      }

      await serviceRegisterVehicle(payload);

      toast.success("Vehicle added successfully!");

      await onVehicleAdded?.();
      onClose();
    } catch (error) {
      console.error("Add vehicle error:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Error: Contact Trucktronix!";

      if (errorMessage.includes("409")) {
        toast.error(`Rego ${newVehicle.vehicleCode.trim()} already exists`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await serverAddVehicle();
  };

  return (
    <div className="avm-shell">
      <button
        type="button"
        className="avm-close-btn"
        onClick={onClose}
        disabled={updating}
        aria-label="Close add vehicle form"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="avm-card">
        <div className="avm-top-row">
          <div className="avm-header">
            <div className="avm-photo-section">
              <button
                type="button"
                className="avm-image-wrap"
                onClick={openFilePicker}
                disabled={updating}
                aria-label={
                  image ? "Change vehicle photo" : "Add vehicle photo"
                }
              >
                {imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt="Selected vehicle preview" />
                ) : (
                  <span className="avm-image-placeholder">
                    <i className="bi bi-image" />
                    <span>Add Photo</span>
                  </span>
                )}

                <span className="avm-image-overlay">
                  <i className="bi bi-camera-fill" />
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </div>

            <div className="avm-header-info">
              <span className="avm-title">Add New Vehicle</span>

              <p className="avm-subtitle">Register your new fleet asset</p>

              <div className="avm-meta">
                <p>
                  <span>Status</span>

                  <span className="avm-meta-value">New asset</span>
                </p>

                <p>
                  <span>Photo</span>

                  <span className="avm-meta-value">
                    {image ? image.name : "Optional"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          id="add-vehicle-form"
          className="avm-data-area"
          onSubmit={handleSubmit}
          noValidate
        >
          <FormSection title="Vehicle Details" icon="bi-truck-front">
            <FormField
              label="Registration"
              htmlFor="avm-vehicle-code"
              urgent={regoUrgent}
              required
            >
              <input
                id="avm-vehicle-code"
                type="text"
                name="vehicleCode"
                placeholder="e.g. TRK-123"
                value={newVehicle.vehicleCode}
                onChange={handleChange}
                disabled={updating}
                autoComplete="off"
                required
              />
            </FormField>

            <FormField
              label="Tracker"
              htmlFor="avm-tracker-code"
              hint="Optional"
            >
              <input
                id="avm-tracker-code"
                type="text"
                name="trackerCode"
                placeholder="Tracker code or IMEI"
                value={newVehicle.trackerCode}
                onChange={handleChange}
                disabled={updating}
                autoComplete="off"
              />
            </FormField>
          </FormSection>

          <FormSection title="Axle Capacity" icon="bi-truck">
            <FormField label="Steer Maximum" htmlFor="avm-steer-weight">
              <input
                id="avm-steer-weight"
                type="number"
                name="steerMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.steerMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Drive Maximum" htmlFor="avm-drive-weight">
              <input
                id="avm-drive-weight"
                type="number"
                name="driveMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.driveMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Trailer A" htmlFor="avm-trailer-a-weight">
              <input
                id="avm-trailer-a-weight"
                type="number"
                name="trailerAMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.trailerAMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Trailer B" htmlFor="avm-trailer-b-weight">
              <input
                id="avm-trailer-b-weight"
                type="number"
                name="trailerBMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.trailerBMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>
          </FormSection>

          <FormSection title="Specifications" icon="bi-sliders">
            <FormField label="Weight Unit" htmlFor="avm-weight-unit">
              <select
                id="avm-weight-unit"
                name="weightUnit"
                value={newVehicle.weightUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="kg">Kilograms (kg)</option>

                <option value="lb">Pounds (lb)</option>
              </select>
            </FormField>

            <FormField label="Fuel Capacity" htmlFor="avm-fuel">
              <input
                id="avm-fuel"
                type="number"
                name="fuel"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.fuel}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Fuel Unit" htmlFor="avm-fuel-unit">
              <select
                id="avm-fuel-unit"
                name="fuelUnit"
                value={newVehicle.fuelUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="liters">Litres</option>

                <option value="gallons">Gallons</option>
              </select>
            </FormField>

            <FormField label="Mileage" htmlFor="avm-mileage">
              <input
                id="avm-mileage"
                type="number"
                name="mileage"
                min="0"
                step="any"
                placeholder="Maximum kilometres"
                value={newVehicle.mileage}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Working Voltage" htmlFor="avm-voltage">
              <input
                id="avm-voltage"
                type="number"
                name="voltage"
                min="0"
                step="any"
                placeholder="0"
                value={newVehicle.voltage}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Voltage Unit" htmlFor="avm-voltage-unit">
              <select
                id="avm-voltage-unit"
                name="voltageUnit"
                value={newVehicle.voltageUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="volt">Volts</option>
              </select>
            </FormField>
          </FormSection>

          <FormSection title="Scale Service" icon="bi-calendar">
            <FormField label="Last Serviced" htmlFor="avm-serviced-on">
              <input
                id="avm-serviced-on"
                type="date"
                name="servicedOn"
                max={currentDate}
                value={newVehicle.servicedOn}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Next Service Due" htmlFor="avm-next-service-on">
              <input
                id="avm-next-service-on"
                type="date"
                name="nextServiceOn"
                min={newVehicle.servicedOn || currentDate}
                value={newVehicle.nextServiceOn}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>
          </FormSection>
        </form>
      </div>

      <footer className="avm-footer">
        <div className="avm-footer-inner">
          <div className="avm-actions">
            <button
              type="submit"
              form="add-vehicle-form"
              className="avm-submit-cta"
              disabled={updating}
            >
              {updating ? (
                <>
                  <span className="avm-spinner" aria-hidden="true" />

                  <span>Processing...</span>
                </>
              ) : (
                <span>Add Vehicle</span>
              )}
            </button>

            <button
              type="button"
              className="avm-cancel-btn"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FormSection = ({ title, icon, children }) => (
  <section className="avm-section">
    <header>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </header>

    <div className="avm-grid">{children}</div>
  </section>
);

const FormField = ({
  label,
  htmlFor,
  hint,
  required = false,
  urgent = false,
  children,
}) => (
  <div className={`avm-field ${urgent ? "urgent" : ""}`}>
    <label htmlFor={htmlFor}>
      <span>{label}</span>

      {required && (
        <span className="avm-required" aria-hidden="true">
          *
        </span>
      )}

      {hint && <span className="avm-field-hint">{hint}</span>}
    </label>

    <div className="avm-control">{children}</div>
  </div>
);

export default AddVehicleModal;
